import React, { useContext, useEffect, useState } from 'react';
import { getTenantTranslationMemoryResources } from '../../../../TranslationResources/services/api-calls';
import { TenantIdContext } from '../../../../../components/app/App';
import { StepEditor } from './StepEditor';
import Select, { SelectOption } from '@amzn/meridian/select';
import LanguageSelect from '../../../../../global/language-select/LanguageSelect';

const TM_EDIT_HEADER = 'Edit Translation Memory:';
const TM_MODELS_LABEL = 'Translation Memory Resource';
const PREFERRED_SOURCE_LABEL = 'TM Source Locale:';
const PREFERRED_TARGET_LABEL = 'TM Target Locale:';

export const TranslationMemoryEditor = ({ subWorkflow, node, onAccept, onCancel }) => {
  const tenantId = useContext(TenantIdContext);
  const [tmResources, setTmResources] = useState([]);
  const [header, setHeader] = useState(TM_EDIT_HEADER);
  const [selectedTm, setSelectedTm] = useState(null);
  const [preferredSourceLocale, setPreferredSourceLocale] = useState('');
  const [preferredTargetLocale, setPreferredTargetLocale] = useState('');
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    setHeader(`${TM_EDIT_HEADER} ${subWorkflow.sourceLocale} to ${subWorkflow.targetLocale}`);
    getTenantTranslationMemoryResources({ tenantId: tenantId }).then(response => {
      setTmResources(
        response.data.map((entry, index) => {
          return {
            ...entry['resourceData'],
            resourceId: entry['resourceSk'],
          };
        })
      );
    });
  }, [node, subWorkflow, tenantId]);

  useEffect(() => {
    if (node.parameters.preferredSourceLocale) {
      setPreferredSourceLocale(node.parameters.preferredSourceLocale);
      setPreferredTargetLocale(node.parameters.preferredTargetLocale);
    }
  }, [node]);

  useEffect(() => {
    if (tmResources === null || tmResources.length === 0) {
      return;
    }

    for (const [index, resource] of tmResources.entries()) {
      if (resource.resourceId === node.parameters.resourceId) {
        setSelectedTm(index);
        return;
      }
    }
  }, [node, tmResources]);

  const handleAcceptClicked = () => {
    const tmResource = tmResources[selectedTm];
    node.parameters.tmLookupTableName = tmResource.tmTableName;
    node.parameters.tmIdentifier = tmResource.tmIdentifier;
    node.parameters.roleArn = tmResource.roleArn;
    node.parameters.resourceId = tmResource.resourceId;
    node.parameters.preferredSourceLocale = preferredSourceLocale;
    node.parameters.preferredTargetLocale = preferredTargetLocale;
    onAccept();
  };

  useEffect(() => {
    let valid = true;
    if (
      (preferredSourceLocale !== '' && preferredTargetLocale === '') ||
      (preferredSourceLocale === '' && preferredTargetLocale !== '') ||
      (preferredSourceLocale !== '' && preferredSourceLocale === preferredTargetLocale)
    ) {
      valid = false;
    }
    setIsValid(valid);
  }, [preferredSourceLocale, preferredTargetLocale]);

  const updateSelections = value => {
    setSelectedTm(value);
  };

  return (
    <StepEditor
      modalHeader={header}
      onAccept={handleAcceptClicked}
      onCancel={onCancel}
      initialRoleArn={node.parameters.roleArn}
      isValid={isValid}
    >
      <div>
        <div>
          <label>{TM_MODELS_LABEL}</label>
          <Select value={selectedTm} onChange={updateSelections} placeholder="Select a translation memory table">
            {tmResources &&
              tmResources.map((resource, resourceIndex) => {
                let label = resource.tmTableName;
                if (resource.tmIdentifier) {
                  label = `${resource.tmTableName}: ${resource.tmIdentifier} [${resource.awsAccountId}]`;
                }
                return <SelectOption value={resourceIndex} key={resourceIndex} label={label} />;
              })}
          </Select>
        </div>
        <div>
          <div>
            <label>{PREFERRED_SOURCE_LABEL}</label>
            <LanguageSelect
              value={preferredSourceLocale}
              isDefaultOptionSelectable
              onChange={setPreferredSourceLocale}
            />
          </div>
          <div>
            <label>{PREFERRED_TARGET_LABEL}</label>
            <LanguageSelect
              value={preferredTargetLocale}
              isDefaultOptionSelectable
              onChange={setPreferredTargetLocale}
            />
          </div>
        </div>
      </div>
    </StepEditor>
  );
};
